import { useState, useRef } from "react";
import { ArrowLeft, Upload, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Link } from "@inertiajs/react";
import AppLayout from "../../layouts/AppLayout";
import CandidateForm, { CandidateFormData } from "./_CandidateForm";
import { ResumeIntakeResponse, ResumeIntakeResult } from "./_types";
import { useAiJobStatus } from "../../hooks/useAiJobStatus";

interface Firm {
  id: number;
  name: string;
}

interface Props {
  firms: Firm[];
  ai_assist: boolean;
}

type ParseStatus = "idle" | "loading" | "success" | "error";

function ResumeIntakeCard({
  aiAssist,
  onSuccess,
  onBlobCreated,
}: {
  aiAssist: boolean;
  onSuccess: (fields: Partial<CandidateFormData>) => void;
  onBlobCreated: (blobId: string) => void;
}) {
  const [status, setStatus] = useState<ParseStatus>("idle");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [jobId, setJobId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // task 2dc337d2 Slice 2 — intake runs as a background job now (finding #38
  // INV-1: parsed fields only ever live in the job's result payload here,
  // never written to a Candidate record — there isn't one yet on this page).
  useAiJobStatus(jobId, {
    onReady: (result) => {
      const parsed = result as unknown as ResumeIntakeResult;
      const formFields = Object.fromEntries(
        Object.entries(parsed.fields)
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)])
      ) as Partial<CandidateFormData>;

      setWarnings(parsed.warnings);
      setStatus("success");
      onSuccess(formFields);
      setJobId(null);
    },
    onFailed: (err) => {
      setError(err ?? "Parsing failed. Please fill in fields manually.");
      setStatus("error");
      setJobId(null);
    },
  });

  const ALLOWED_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  function isAllowedFile(file: File): boolean {
    if (ALLOWED_TYPES.includes(file.type)) return true;
    const name = file.name.toLowerCase();
    return name.endsWith(".pdf") || name.endsWith(".docx");
  }

  async function handleFile(file: File) {
    if (!isAllowedFile(file)) {
      setError("Please upload a PDF or DOCX file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File must be 5 MB or smaller.");
      return;
    }

    setStatus("loading");
    setError(null);
    setWarnings([]);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
      const res = await fetch("/candidates/intake", {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": token },
      });

      const body = await res.json().catch(() => ({})) as ResumeIntakeResponse & { error?: string };

      // Always capture the blob ID when the server persisted the file
      if (body.resume_blob_id) onBlobCreated(body.resume_blob_id);

      if (!res.ok || !body.ai_job_status_id) {
        setError(body.error ?? "Parsing failed. Please fill in fields manually.");
        setStatus("error");
        return;
      }

      // Stays "loading" — useAiJobStatus's onReady/onFailed above take it
      // from here once the background parse finishes.
      setJobId(body.ai_job_status_id);
    } catch {
      setError("Network error. Please try again.");
      setStatus("error");
    }
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  return (
    <div className="mb-8">
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragging
            ? "border-green-400 bg-green-50"
            : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
        }`}
      >
        <input ref={inputRef} type="file" accept=".pdf,.docx" className="hidden" onChange={handleChange} />

        {status === "loading" && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Parsing resume...
          </div>
        )}
        {status === "success" && (
          <div className="flex items-center justify-center gap-2 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" />
            Resume parsed — form pre-filled. Drop another to re-parse.
          </div>
        )}
        {(status === "idle" || status === "error") && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
            <Upload className="w-4 h-4" />
            Drop a resume (PDF or DOCX) or click to browse — optional
          </div>
        )}
      </div>

      {!aiAssist && status === "success" && (
        <p className="mt-2 text-xs text-amber-600">
          AI Assist is off — only basic contact and education fields were auto-filled. Review the form carefully before saving.
        </p>
      )}

      {warnings.length > 0 && (
        <div className="mt-2 space-y-1">
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-600">{w}</p>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}

export default function CandidateNew({ firms, ai_assist }: Props) {
  const [intakeFields, setIntakeFields] = useState<Partial<CandidateFormData> | undefined>(undefined);
  const [formKey, setFormKey] = useState(0);
  const [resumeBlobId, setResumeBlobId] = useState<string | undefined>(undefined);

  function handleIntakeSuccess(fields: Partial<CandidateFormData>) {
    setIntakeFields(fields);
    setFormKey((k) => k + 1);
  }

  return (
    <AppLayout title="Add Candidate">
      <div className="max-w-3xl mx-auto">
        <Link href="/candidates" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Candidates
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Add Candidate</h1>
        <ResumeIntakeCard aiAssist={ai_assist} onSuccess={handleIntakeSuccess} onBlobCreated={setResumeBlobId} />
        <CandidateForm
          key={formKey}
          initialValues={intakeFields}
          resumeBlobId={resumeBlobId}
          firms={firms}
          submitUrl="/candidates"
          method="post"
          submitLabel="Add Candidate"
          cancelUrl="/candidates"
        />
      </div>
    </AppLayout>
  );
}
